import { post } from "@selector/shared";
import { type components, Content } from "@selector/shared/openapi";
import type { GridApi } from "ag-grid-enterprise";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import type { ColumnRow } from "../ColumnSelectionGrid.tsx";
import type { TreeColumnType } from "../ColumnSelectionPopover.tsx";
import { filterOutFrontendOnlyColumns } from "../utils";
import type { TableRequest } from "./ColumnTemplatesPanel.tsx";
import { ColumnTemplatesContext, type ColumnTemplatesState } from "./context.ts";
import { columnsToRows, mergeColumnRows } from "./utils";

export interface ColumnTemplatesProviderProps {
  tableSpecForTemplates: Partial<components["schemas"]["TableRequest"]>;
  children: ReactNode;
  baseRequest: Partial<TableRequest>;
  isOpen: boolean;
  initialValueColumns?: TreeColumnType[] | null;
}

const areSelectionsEqual = (left: string[] | undefined, right: string[] | undefined) => {
  if (!left || !right) {
    return false;
  }

  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const filterOutFixedColumns = (columns: TreeColumnType[] | null | undefined): TreeColumnType[] | undefined => {
  if (!columns) {
    return undefined;
  }

  return columns
    .filter((column) => !column.fixed)
    .map((column) => ({
      ...column,
      children: column.children?.filter((child) => child != null && !child.fixed),
    }));
};

export const ColumnTemplatesProvider = ({
  children,
  baseRequest,
  isOpen,
  tableSpecForTemplates,
  initialValueColumns,
}: ColumnTemplatesProviderProps) => {
  const gridApi = useRef<GridApi<ColumnRow> | null>(null);
  const [allColumns, setAllColumns] = useState<TreeColumnType[] | null>(null);
  const [templateColumns, setTemplateColumns] = useState<TreeColumnType[] | null>(null);
  const [currentColumnSelection, setColumnSelection] = useState<string[]>([]);

  const setGridApi = (api: GridApi<ColumnRow>) => {
    gridApi.current = api;
  };

  const allColumnsRows = useMemo(() => columnsToRows(allColumns), [allColumns]);
  const templateRows = useMemo(() => columnsToRows(templateColumns), [templateColumns]);
  const columnsRows = useMemo(() => mergeColumnRows(allColumnsRows, templateRows), [allColumnsRows, templateRows]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const requestBody = {
      ...baseRequest,
      content: [Content.ALL_COLUMNS],
    } satisfies Partial<TableRequest>;

    post("/table", undefined, undefined, requestBody as TableRequest).then(({ data: { allColumns } }) => {
      const sanitizedInitial = filterOutFrontendOnlyColumns(filterOutFixedColumns(initialValueColumns));
      const filteredAllColumns = filterOutFrontendOnlyColumns(allColumns);

      setAllColumns(filteredAllColumns);
      setTemplateColumns(sanitizedInitial.length > 0 ? sanitizedInitial : null);

      // Use the provided initial columns when available. Fall back to the server payload to avoid an empty selection
      // when the parent component hasn't supplied its own snapshot yet (e.g., immediately after a table reset).
      const mergedRowsForSelection = mergeColumnRows(
        columnsToRows(filteredAllColumns),
        columnsToRows(sanitizedInitial.length > 0 ? sanitizedInitial : null),
      );
      const selectionRows = sanitizedInitial.length > 0 ? columnsToRows(sanitizedInitial) : mergedRowsForSelection;
      const visibleColumns =
        selectionRows?.filter((c) => !c?.isGroup && !c?.fixed && !c?.hide && c?.colId).map((c) => c.colId!) ?? [];

      // We need to wait for the `allColumns` to propagate to the grid, that's why we apply the selection with `onFirstDataRendered`.
      // It feels like a hack, but that's the best way I found so far.
      setColumnSelection((prev) => (areSelectionsEqual(prev, visibleColumns) ? prev : visibleColumns));
    });
  }, [baseRequest, initialValueColumns, isOpen]);

  const value: ColumnTemplatesState = useMemo(
    () => ({
      columnsRows,
      gridApi,
      setGridApi,
      tableSpecForTemplates,
      currentColumnSelection,
      setColumnSelection,
      setTemplateColumns,
    }),
    [columnsRows, currentColumnSelection, tableSpecForTemplates],
  );

  return <ColumnTemplatesContext.Provider value={value}>{children}</ColumnTemplatesContext.Provider>;
};
